import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "../components/legal/LegalLayout";

const CommunityGuidelines = () => (
  <LegalLayout title="Community Guidelines" testId="community-guidelines-content">
    <h1>Community Guidelines – Thryvin'</h1>
    <p className="last-updated">Last updated: 15 October 2026 (guidelines version 2026-10-14.v3)</p>

    <p className="intro">
      Community is the part of the Thryvin' app where friends share workouts, PBs and recipes and react to each other's
      sessions. It's optional and only for adults aged 18 or over. You accept these guidelines when you join, and they
      form part of our{" "}
      <Link to="/terms" className="inline-link">
        Terms and Conditions
      </Link>
      .
    </p>

    <section>
      <h2>The short version</h2>
      <p>These are the same points you agree to in the app when you join:</p>
      <ul>
        <li>Be kind. No hate, harassment, threats or bullying.</li>
        <li>No sexual content or nudity, and nothing involving anyone under 18. Ever.</li>
        <li>Reaction photos are of you only. Don't share other people's reactions outside the app.</li>
        <li>No promoting self-harm, eating disorders, steroids, drugs or extreme diets.</li>
        <li>No spam, ads, links or other people's private details.</li>
        <li>Report anything that breaks these rules. We review every report.</li>
        <li>
          Photos and posts are automatically checked for safety by an automated service (OpenAI moderation) before
          others see them.
        </li>
        <li>
          Your profile photo is optional, must be of you, gets the same safety check, and only your friends see it.
          Everyone else sees your initials.
        </li>
      </ul>
    </section>

    <section>
      <h2>What isn't allowed</h2>
      <ul>
        <li>Hate, harassment, bullying and threats</li>
        <li>Sexual content or nudity</li>
        <li>
          Any sexual content involving children. We have zero tolerance for this: the account is banned and we report it
          to the authorities (see our{" "}
          <Link to="/child-safety" className="inline-link">
            Child Safety Standards
          </Link>
          )
        </li>
        <li>Promoting self-harm or eating disorders</li>
        <li>Selling or promoting steroids or drugs</li>
        <li>Extreme diet advice</li>
        <li>Spam and ads</li>
        <li>Pretending to be someone else</li>
        <li>Other people's private information</li>
        <li>Photos of other people in Live reactions, and sharing other people's reactions outside the app</li>
      </ul>
      <p>
        Nicknames, captions and comments go through a word filter as you type, so swearing, slurs, links, email
        addresses, phone numbers and @handles can't be posted.
      </p>
    </section>

    <section>
      <h2>What's shared, and what never is</h2>
      <p>
        Only friends you accept see your posts. You choose what posts automatically (finished workouts and new PBs each
        have their own switch). Your weight, body measurements, food logs, calories, health data, injuries, coach chats,
        age, email and real name are never shared.
      </p>
    </section>

    <section>
      <h2>Reporting and blocking</h2>
      <ul>
        <li>
          <strong>Report:</strong> tap ⋯ on any post, comment, reaction photo or profile, then Report. Reasons include
          spam, harassment or bullying, hate, sexual content, self-harm or eating-disorder content, dangerous advice,
          pretending to be someone, and "seems under 18".
        </li>
        <li>
          <strong>Block:</strong> from any profile, post or comment. Blocked people disappear from each other's
          Community, and they aren't told.
        </li>
        <li>
          <strong>Email:</strong>{" "}
          <a href="mailto:support@thryvin.app" className="inline-link">
            support@thryvin.app
          </a>
        </li>
      </ul>
      <p>
        When you report something, you stop seeing it straight away. Content is hidden from everyone while we review it
        if three people report a post or comment, two people report a photo, or anyone reports self-harm, sexual content
        or someone under 18.
      </p>
      <p>
        We aim to review reports about self-harm, sexual content, anyone under 18, threats and anything with a photo
        within <strong>24 hours</strong>, and everything else within <strong>72 hours</strong>.
      </p>
      <p>
        If a report is about self-harm or an eating disorder, the person is shown support links, such as Samaritans
        (116 123) and Beat.
      </p>
    </section>

    <section>
      <h2>What happens if the rules are broken</h2>
      <p>
        Depending on how serious it is, we may remove the content, send a warning, pause your Community access for 7
        days, remove you from Community, or close your account. Two removals within 30 days means a 7-day pause, and a
        third means removal from Community. Child sexual abuse material or threats mean the account is closed straight
        away.
      </p>
    </section>

    <section>
      <h2>Appeals</h2>
      <p>
        If we remove something of yours, we tell you why. You can appeal within 14 days by replying to the notice or
        emailing{" "}
        <a href="mailto:support@thryvin.app" className="inline-link">
          support@thryvin.app
        </a>
        , and we'll answer within 7 days.
      </p>
    </section>

    <section>
      <h2>Contact</h2>
      <p>
        <a href="mailto:support@thryvin.app" className="inline-link">
          support@thryvin.app
        </a>{" "}
        · Jake West (Thryvin'), United Kingdom
      </p>
    </section>
  </LegalLayout>
);

export default CommunityGuidelines;
